/**
 * Utilitários do mapa do Project Hub (Leaflet / OSM).
 * Guia: Mapa e Coordenadas — Frontend Vert Ecotech
 */

export const MAP_SOURCE_COLORS = {
  geographic_coordinate: "#006693",
  ema_station: "#00b056",
  alert: "#D14343",
  firms_scan: "#ff8c00",
  alert_request_firms: "#7c3aed",
};

export const MAP_SOURCE_LABELS = {
  geographic_coordinate: "Coordenada cadastrada",
  ema_station: "Estação EMA",
  alert: "Alerta",
  firms_scan: "Detecção FIRMS",
  alert_request_firms: "Solicitação (FIRMS)",
};

export const MAP_LAYER_LABELS = {
  reference: "Coordenadas cadastradas / EMA",
  alerts: "Alertas",
  requests: "Solicitações",
  firmsDetections: "Detecções FIRMS",
  firmsAreas: "Áreas FIRMS (polígonos)",
};

export const FIRMS_LAYER_STYLE = {
  strokeColor: "#ff8c00",
  strokeOpacity: 0.85,
  strokeWeight: 2,
  fillColor: "#ff8c00",
  fillOpacity: 0.12,
};

/** Cuiabá / MT — fallback sem dados */
export const DEFAULT_MAP_CENTER = { lat: -15.6014, lng: -56.0979 };

export function getSourceColor(source) {
  return MAP_SOURCE_COLORS[source] || "#3A70B2";
}

export function getSourceLabel(source) {
  return MAP_SOURCE_LABELS[source] || source || "Ponto";
}

/** Pontos cujo `label` é código de alerta ou solicitação (clicável na tabela). */
export const CLICKABLE_MAP_CODE_SOURCES = new Set([
  "alert",
  "alert_request_firms",
]);

export function isClickableMapCodePoint(point) {
  if (!point || !CLICKABLE_MAP_CODE_SOURCES.has(point.source)) return false;
  const label = String(point.label || "").trim();
  return label.length > 0;
}

export function isAlertRequestMapSource(source) {
  return source === "alert_request_firms";
}

export function isAlertMapSource(source) {
  return source === "alert";
}

/** WKT POLYGON usa lon lat; Leaflet usa lat, lng */
export function parsePolygonWkt(wkt) {
  if (!wkt?.trim()) return [];
  const match = wkt.match(/POLYGON\s*\(\(([^)]+)\)\)/i);
  if (!match) return [];

  return match[1]
    .split(",")
    .map((pair) => pair.trim())
    .map((pair) => {
      const parts = pair.split(/\s+/).map(Number);
      if (parts.length < 2) return null;
      const [lon, lat] = parts;
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      return { lat, lng: lon };
    })
    .filter(Boolean);
}

export function bboxToPolygonPaths(bbox) {
  if (!bbox) return [];
  const { west, south, east, north } = bbox;
  if (
    ![west, south, east, north].every(
      (v) => typeof v === "number" && Number.isFinite(v)
    )
  ) {
    return [];
  }
  return [
    { lat: south, lng: west },
    { lat: south, lng: east },
    { lat: north, lng: east },
    { lat: north, lng: west },
  ];
}

export function getLayerPaths(layer) {
  const fromWkt = parsePolygonWkt(layer?.polygon_wkt);
  if (fromWkt.length >= 3) return fromWkt;
  return bboxToPolygonPaths(layer?.bbox);
}

export function computeMapBounds(points = [], layers = []) {
  let north = -Infinity;
  let south = Infinity;
  let east = -Infinity;
  let west = Infinity;
  let has = false;

  for (const point of points) {
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lon)) continue;
    north = Math.max(north, point.lat);
    south = Math.min(south, point.lat);
    east = Math.max(east, point.lon);
    west = Math.min(west, point.lon);
    has = true;
  }

  for (const layer of layers) {
    const bbox = layer?.bbox;
    if (!bbox) continue;
    north = Math.max(north, bbox.north);
    south = Math.min(south, bbox.south);
    east = Math.max(east, bbox.east);
    west = Math.min(west, bbox.west);
    has = true;
  }

  if (!has) return null;
  return { north, south, east, west };
}

export function pointKey(point, index) {
  return `${point.source}-${point.lat}-${point.lon}-${point.label || ""}-${index}`;
}

export function emptyHubMap() {
  return {
    reference_points: [],
    alert_points: [],
    alert_request_points: [],
    firms_layers: [],
    firms_detection_points: [],
    all_points: [],
    summary: {
      reference_points_count: 0,
      alert_points_count: 0,
      alert_request_points_count: 0,
      firms_detection_points_count: 0,
      firms_layers_count: 0,
      all_points_count: 0,
    },
  };
}

/**
 * Contadores da legenda: prioriza map.summary; senão length dos arrays.
 */
export function getHubMapLayerCounts(map) {
  const m = map || emptyHubMap();
  const summary = m.summary && typeof m.summary === "object" ? m.summary : {};

  const pick = (summaryKey, arr) => {
    const fromSummary = summary[summaryKey];
    if (typeof fromSummary === "number" && Number.isFinite(fromSummary)) {
      return fromSummary;
    }
    return Array.isArray(arr) ? arr.length : 0;
  };

  return {
    reference: pick("reference_points_count", m.reference_points),
    alerts: pick("alert_points_count", m.alert_points),
    requests: pick("alert_request_points_count", m.alert_request_points),
    firmsDetections: pick(
      "firms_detection_points_count",
      m.firms_detection_points
    ),
    firmsAreas: pick("firms_layers_count", m.firms_layers),
    allPoints: pick("all_points_count", m.all_points),
  };
}

/**
 * Se o backend omitiu focos em map.firms_detection_points mas enviou
 * map_points em firms_scans / guardian_areas[].firms_areas, reconstrói.
 */
export function enrichHubMapFromScans(map, firmsScans = [], guardianAreas = []) {
  const base = {
    ...emptyHubMap(),
    ...(map && typeof map === "object" ? map : {}),
  };

  const existing = Array.isArray(base.firms_detection_points)
    ? base.firms_detection_points
    : [];
  if (existing.length > 0) {
    return base;
  }

  const seen = new Set();
  const collected = [];

  const add = (point) => {
    if (!point || point.lat == null || point.lon == null) return;
    const key = `${Number(point.lat).toFixed(6)}:${Number(point.lon).toFixed(6)}:${point.scan_id || ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    collected.push({
      ...point,
      source: point.source || "firms_scan",
    });
  };

  for (const row of firmsScans || []) {
    for (const p of row?.map_points || []) add(p);
  }

  for (const block of guardianAreas || []) {
    const firmsAreas = block?.firms_areas || [];
    for (const fa of firmsAreas) {
      for (const p of fa?.map_points || []) add(p);
    }
  }

  if (collected.length === 0) {
    return base;
  }

  const reference = Array.isArray(base.reference_points)
    ? base.reference_points
    : [];
  const alerts = Array.isArray(base.alert_points) ? base.alert_points : [];
  const requests = Array.isArray(base.alert_request_points)
    ? base.alert_request_points
    : [];
  const layers = Array.isArray(base.firms_layers) ? base.firms_layers : [];
  const allPoints = [...reference, ...alerts, ...requests, ...collected];

  return {
    ...base,
    firms_detection_points: collected,
    all_points: allPoints,
    summary: {
      ...(base.summary || {}),
      reference_points_count: reference.length,
      alert_points_count: alerts.length,
      alert_request_points_count: requests.length,
      firms_detection_points_count: collected.length,
      firms_layers_count: layers.length,
      all_points_count: allPoints.length,
    },
  };
}
